// src/utils/zonas.ts

/**
 * Zonas de la tabla de asistencias.
 *
 * Los umbrales (75% y 25%) son los que ya aplica el backend en eventos.js para
 * elegir el color de cada jugador; acá se reutilizan para agrupar.
 */
export type Zona = "alta" | "media" | "descenso" | "sin-datos";

/** Orden en el que se muestran. Sin eventos va al final: no compite. */
export const ORDEN_ZONA: Record<Zona, number> = {
  alta: 0,
  media: 1,
  descenso: 2,
  "sin-datos": 3,
};

export const zonaPorPorcentaje = (
  porcentaje: number,
  totalEventos: number
): Zona => {
  if (totalEventos === 0) return "sin-datos";
  const pct = Math.round(porcentaje || 0);
  if (pct >= 75) return "alta";
  if (pct >= 25) return "media";
  return "descenso";
};

/**
 * Ordena por zona y, dentro de cada zona, por porcentaje descendente.
 *
 * Ordenar sólo por porcentaje no alcanza, porque la zona no depende sólo del
 * porcentaje: un jugador dado de baja llega marcado como "sin datos" y su
 * porcentaje lo dejaba en el medio del descenso, partiendo el bloque en dos y
 * dibujando el separador dos veces. Con esto cada zona es contigua por
 * construcción y cada rótulo aparece una sola vez.
 */
export function compararPorZona<T>(
  zonaDe: (item: T) => Zona,
  porcentajeDe: (item: T) => number
) {
  return (a: T, b: T) => {
    const porZona = ORDEN_ZONA[zonaDe(a)] - ORDEN_ZONA[zonaDe(b)];
    if (porZona !== 0) return porZona;
    return porcentajeDe(b) - porcentajeDe(a);
  };
}

/** Lo mínimo que hace falta saber de un jugador para el último puesto. */
export interface DatosDescenso {
  porcentaje: number;
  totalEventos: number;
  dadoDeBaja?: boolean;
}

/**
 * Para pelear el último puesto hay que haber tenido eventos y seguir activo.
 *
 * Sin esto, un jugador recién dado de alta (0 de 0) o uno dado de baja
 * empatarían el fondo de la tabla sin haber jugado nada.
 */
export const compiteEnLaTabla = (datos: DatosDescenso) =>
  datos.totalEventos > 0 && !datos.dadoDeBaja;

/**
 * Porcentaje del último puesto, o null si todavía no hay a quién medir.
 *
 * El descenso es el último puesto —con sus empates—, no una franja de
 * porcentaje: que varios estén en rojo no los pone a todos en descenso.
 */
export function porcentajeDeDescenso<T>(
  items: T[],
  datos: (item: T) => DatosDescenso
): number | null {
  const compiten = items.map(datos).filter(compiteEnLaTabla);
  if (compiten.length === 0) return null;
  return Math.min(...compiten.map((d) => d.porcentaje));
}
